// PZ Document Types and Interfaces (Przyjecie Zewnetrzne - Warehouse Receipt)
import type { TransportRouteSummaryDto } from '../dto/transport';

export enum PZStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED'
}

export interface PZDocument {
  id: string;
  pzNumber: string;
  orderId?: string;
  supplierId?: string;
  supplier?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    taxId?: string;
  };
  issueDate: string;
  receiptDate: string;
  expectedDeliveryDate?: string;
  supplierName?: string;
  supplierAddress?: string;
  supplierNIP?: string;
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  supplierInvoiceAmount?: number;
  supplierInvoiceCurrency?: string;
  recipientName?: string;
  recipientAddress?: string;
  recipientNIP?: string;
  warehouseLocation?: string;
  sourceTransportId?: string | null;
  sourceTransportIds?: string[];
  sourceTransports?: TransportRouteSummaryDto[];
  transportCompany?: string;
  transportInvoiceNo?: string;
  transportCost?: number;
  transportCurrency?: string;
  vehicleRegistration?: string;
  driverName?: string;
  driverPhone?: string;
  totalQuantity?: number;
  totalNetWeight?: number;
  totalGrossWeight?: number;
  totalValue?: number;
  currency: string;
  allocateTransportCost?: boolean;
  allocateCustomsCost?: boolean;
  status: PZStatus;
  approvedBy?: string;
  receivedBy?: string;
  confirmedAt?: string;
  receivedAt?: string;
  notes?: string;
  internalNotes?: string;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    email: string;
    name?: string;
  };
  items?: PZItem[];
  attachments?: PZAttachment[];
}

export interface PZItem {
  id: string;
  pzDocumentId: string;
  productVariantId?: string;
  productName?: string;
  productCode?: string;
  sku?: string;
  ean?: string;
  unit?: string;
  quantityOrdered: number;
  // NULL = not yet entered (backend falls back to quantityOrdered when
  // stocking); explicit 0 stocks zero units. See migration 097.
  quantityReceived: number | null;
  quantityDamaged?: number;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  // Per-line secondary unit auto-computed from dimensions (migration 084).
  // null = pieces only.
  conversionUnit?: 'm2' | 'm3' | null;
  unitPrice?: number;
  totalPrice?: number;
  exchangeRatePLN?: number;
  unitPricePLN?: number;
  totalPricePLN?: number;
  currency: string;
  allocatedTransportCost?: number;
  allocatedCustomsCost?: number;
  allocatedOtherCost?: number;
  totalUnitCost?: number;
  cnCode?: string;
  countryOfOrigin?: string;
  description?: string;
  notes?: string;
  productVariant?: {
    id: string;
    sku: string;
    variantName: string;
    width?: number | null;
    height?: number | null;
    length?: number | null;
    thickness?: number | null;
    netWeight?: number | null;
    grossWeight?: number | null;
    product?: {
      id: string;
      code: string;
      name: string;
      ean?: string | null;
      category?: string;
      productCategoryId?: string | null;
      productCategoryName?: string | null;
      productCategoryColor?: string | null;
      productCategoryDimensionDisplayProfile?: string | null;
      baseUnit?: string;
      width?: number | null;
      height?: number | null;
      length?: number | null;
      thickness?: number | null;
    };
  };
}

export interface PZAttachment {
  id: string;
  pzId: string;
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  description?: string;
  category?: string;
  uploadedAt: string;
  uploadedBy?: string;
  user?: {
    name: string;
    email: string;
  };
}

export interface CreatePZDto {
  pzNumber?: string;
  receiptDate: string;
  supplierId: string;
  currency: string;
  sourceTransportId?: string | null;
  sourceTransportIds?: string[];
  transportCompany?: string;
  transportInvoiceNo?: string;
  allocateTransportCost?: boolean;
  transportCost?: number;
  transportCurrency?: string;
  vehicleRegistration?: string;
  driverName?: string;
  driverPhone?: string;
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  supplierInvoiceAmount?: number;
  supplierInvoiceCurrency?: string;
  warehouseLocation?: string;
  approvedBy?: string | null;
  receivedBy?: string | null;
  notes?: string;
  items: {
    id?: string;
    productVariantId: string;
    quantityOrdered: number;
    quantityReceived?: number | null;
    width?: number | null;
    height?: number | null;
    length?: number | null;
    thickness?: number | null;
    netWeight?: number | null;
    grossWeight?: number | null;
    conversionUnit?: 'm2' | 'm3' | null;
    unitPrice?: number;
    exchangeRatePLN?: number;
    currency: string;
    ean?: string;
    notes?: string;
  }[];
}

export interface UpdatePZDto extends Partial<CreatePZDto> {
  status?: PZStatus;
}

export interface PZFilters {
  status?: PZStatus[];
  dateRange?: [string | null, string | null];
  search?: string;
  supplierId?: string;
}

// Inventory Types
export interface Inventory {
  id: string;
  productVariantId: string;
  quantityAvailable: number;
  quantityInTransit: number;
  quantityReserved: number;
  quantityTotal: number;
  averageCostPLN?: number;
  totalValuePLN?: number;
  status: string;
  lastMovementAt?: string;
  lastMovementType?: string;
  productVariant?: {
    id: string;
    sku: string;
    variantName: string;
    product?: {
      id: string;
      code: string;
      name: string;
    };
  };
  activeBatches?: InventoryBatch[];
  recentMovements?: InventoryMovement[];
}

export interface InventoryBatch {
  id: string;
  productVariantId: string;
  batchNumber: string;
  pzDocumentId: string;
  quantityReceived: number;
  quantityRemaining: number;
  quantityReserved: number;
  unitCostPLN?: number;
  totalCostPLN?: number;
  receiptDate: string;
  purchaseDate: string;
  status: 'ACTIVE' | 'DEPLETED' | 'EXPIRED';
}

export interface InventoryMovement {
  id: string;
  productVariantId: string;
  // Matches the DB "MovementType" enum (backend/src/services/initService.js).
  // 'ADJUSTMENT' is legacy, pre-096-fix rows: Inventory.lastMovementType is a
  // TEXT column (not enum-constrained) and InventoryDashboard.tsx still
  // renders it as a case alongside ADJUSTMENT_IN/ADJUSTMENT_OUT.
  movementType: 'PZ_IN' | 'WZ_OUT' | 'ADJUSTMENT_IN' | 'ADJUSTMENT_OUT' | 'DAMAGE' | 'RETURN' | 'TRANSFER' | 'INVENTORY_COUNT' | 'ADJUSTMENT';
  quantity: number;
  quantityBefore: number;
  quantityAfter: number;
  unitCostPLN?: number;
  totalCostPLN?: number;
  referenceNumber?: string;
  notes?: string;
  movementDate: string;
  performedBy?: string;
}

export interface Product {
  id: string;
  code: string;
  name: string;
  englishName?: string;
  ean?: string | null;
  description?: string;
  category?: string;
  productCategoryId?: string | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  baseUnit: string;
  cnCode?: string;
  countryOfOrigin?: string;
  netWeight?: number;
  grossWeight?: number;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  // Package-based LDM (logistics) defaults (migration 093). mm for dims.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  defaultWeight?: number;
  defaultPurchasePrice?: number | null;
  defaultPurchaseCurrency?: string | null;
  defaultSalePrice?: number | null;
  defaultSaleCurrency?: string | null;
  minStockLevel?: number | null;
  maxStockLevel?: number | null;
  status: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';
  isActive: boolean;
  variants?: ProductVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  ean?: string;
  variantName: string;
  size?: string;
  color?: string;
  configuration?: string;
  attributes?: Record<string, string>;
  isDefault?: boolean;
  eanOverride?: string | null;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  netWeight?: number;
  grossWeight?: number;
  netWeightOverride?: number | null;
  grossWeightOverride?: number | null;
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  piecesPerPackageOverride?: number | null;
  packageLengthOverride?: number | null;
  packageWidthOverride?: number | null;
  packageStackLayersOverride?: number | null;
  weight?: number;
  defaultPurchasePrice?: number;
  defaultPurchaseCurrency?: string;
  defaultSalePrice?: number;
  defaultSaleCurrency?: string;
  defaultPurchasePriceOverride?: number | null;
  defaultPurchaseCurrencyOverride?: string | null;
  defaultSalePriceOverride?: number | null;
  defaultSaleCurrencyOverride?: string | null;
  basePriceUSD?: number;
  basePricePLN?: number;
  minStockLevel?: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  isActive: boolean;
  product?: Product;
  inventory?: Inventory;
}

export interface SupplierContact {
  name?: string;
  phone?: string;
  email?: string;
}

export interface Supplier {
  id: string;
  name: string;
  contactPerson?: string;
  contactPeople?: SupplierContact[] | null;
  email?: string;
  phone?: string;
  address?: string;
  taxId?: string;
  isActive: boolean;
}
